import { catalog } from '@nolens/i18n';
import type { AuditEntry, SiteMode } from '@nolens/domain';

export const adminText = catalog(
  {
    noAccess: 'No access',
    noAccessHint: 'The admin portal is only for the owner of the application.',
    title: 'Admin',
    hint: "Site rules, logs and platform totals. Each company's internal data doesn't show here.",
    section: 'Section',
    tabSummary: 'Summary and savings',
    tabSites: 'Sites',
    tabLogs: 'Logs',
    estimatedSavings: 'Estimated savings',
    stepsTimes: (n: string) => `${n} steps × 2 min`,
    documentedSteps: 'Documented steps',
    guides: 'Guides',
    accounts: 'Accounts',
    teams: 'Teams',
    unlocks30: 'Unlocks (30 days)',
    totalsNote:
      "It is not a measurement. Totals for the whole platform: each company's data (spaces, people, guides) is only seen by those who belong to it.",
    savingsByMonth: 'Estimated savings per month',
    month: 'Month',
    rulesHint:
      'Sites where the extension does not record. “domain.com” covers the domain and its subdomains; “domain.com/path” only that path. Changes reach the extensions within a few hours (or right away, when connecting the account).',
    ruleExists: 'A rule for that site already exists.',
    newRule: 'New rule',
    site: 'Site',
    sitePlaceholder: 'e.g. intranet.company.com',
    restriction: 'Restriction',
    protected: 'Protected',
    locked: 'Locked',
    note: 'Note',
    noteOptional: 'Note (optional)',
    add: 'Add',
    ruleAdded: 'Rule added.',
    rulesCount: (n: number) => `${n} rules`,
    searchRules: 'Search rules',
    search: 'Search…',
    restrictionOf: (p: string) => `Restriction of ${p}`,
    noteOf: (p: string) => `Note for ${p}`,
    ruleUpdated: 'Rule updated.',
    noteSaved: 'Note saved.',
    removeOf: (p: string) => `Remove ${p}`,
    removeRule: 'Remove the rule?',
    cancel: 'Cancel',
    remove: 'Remove',
    ruleRemoved: 'Rule removed.',
    modeLong: (m: SiteMode): string =>
      m === 'locked'
        ? 'Locked (only the owner unlocks)'
        : 'Protected (each person can unlock, after confirming)',
    modeShort: (m: SiteMode): string => (m === 'locked' ? 'locked' : 'protected'),
    action: (a: AuditEntry['action']): string =>
      ({
        'rule.create': 'Rule created',
        'rule.update': 'Rule changed',
        'rule.delete': 'Rule removed',
        'site.unlock': 'Site unlocked in the extension',
        'site.relock': 'Site locked again in the extension',
        'grant.pro': 'Pro trial given',
        'grant.revoke': 'Pro trial removed',
        'grant.team': 'Team trial extended',
        'beta.settings': 'Beta program settings saved',
        'beta.welcome': 'Welcome email sent',
      })[a] ?? a,
    noteChanged: 'note changed',
    when: 'When',
    who: 'Who',
    what: 'What',
    detail: 'Detail',
    anonymous: 'Anonymous',
    noLogs: 'No logs yet.',
    tabSignups: 'Beta program',
    signupsHint: (n: number) =>
      `${n} ${n === 1 ? 'person' : 'people'} asked to try the beta (page /beta).`,
    noSignups: 'No sign-ups yet.',
    copyEmails: 'Copy emails',
    copied: 'Emails copied.',
    email: 'Email',
    name: 'Name',
    company: 'Company',
    teamSize: 'Team',
    area: 'Area',
    tools: 'Apps they use',
    tabAccess: 'Access',
    target: 'Site or account',
    accessHint:
      'Give testers access without payments: the person must already have an account (signed in once). A paid subscription is never changed.',
    days: 'Days',
    emailPlaceholder: 'name@company.com',
    giveProTitle: 'Pro trial',
    giveProHint: 'Pro plan for one person until the chosen date. Never shortens a longer trial.',
    givePro: 'Give Pro trial',
    proGiven: (email: string, until: string) => `${email} has Pro until ${until}.`,
    teamTitle: 'Extend team trial',
    teamHint: 'Extends the trial of every team the person owns.',
    extendTeam: 'Extend team trial',
    teamExtended: (n: number, until: string) =>
      `${n === 1 ? '1 team' : `${n} teams`} on trial until ${until}.`,
    noAccount: 'There is no account with that email. Ask the person to sign in once first.',
    noTeam: 'That account doesn’t own any team (it must create one first).',
    paidPlan: 'That account has a paid subscription: nothing was changed.',
    grantsTitle: 'Pro given by you',
    noGrants: 'No Pro trials given yet.',
    until: 'Until',
    expired: 'ended',
    revoke: 'Remove',
    revokeOf: (e: string) => `Remove Pro from ${e}`,
    revokeConfirm: (e: string) => `Remove Pro from ${e}? The account goes back to the free plan.`,
    revoked: 'Pro removed.',
    // Programa beta
    tabReports: 'Bug reports',
    betaSettingsTitle: 'Extension link and welcome email',
    betaSettingsHint:
      'When the extension is published, paste its link here. Each person gets the welcome email in their language, with a personal link to report problems.',
    extensionUrl: 'Extension link',
    extensionUrlPlaceholder: 'https://chromewebstore.google.com/detail/…',
    macUrl: 'Mac app download (DMG)',
    macUrlPlaceholder: 'https://downloads.guides.nolens.app/NolensGuides.dmg',
    macUrlHint: 'Shown on the “Mac app” page. Test version only: not notarized yet.',
    language: 'Language',
    langPt: 'Português',
    langEn: 'English',
    subject: 'Subject',
    body: 'Text',
    markers:
      'Markers: {nome} name · {email} · {link_extensao} extension link · {link_entrar} sign-in page · {link_reportar} personal link to report problems.',
    preview: 'Preview (example)',
    previewName: 'Ana',
    save: 'Save',
    saved: 'Saved.',
    restoreDefault: 'Restore the default text',
    sendTest: 'Send a test to me',
    testSent: 'Test sent to your email.',
    unsaved: 'You have unsaved changes: save them before sending.',
    noExtensionUrl: 'Add and save the extension link before sending the welcome email.',
    notConfigured: 'Sending emails is not configured (RESEND_API_KEY).',
    sendSelected: (n: number) => `Send welcome to selected (${n})`,
    sendPending: (n: number) => `Send to everyone not yet sent (${n})`,
    confirmSend: (n: number) => `Send the welcome email to ${n} ${n === 1 ? 'person' : 'people'}?`,
    send: 'Send',
    sendOne: 'Send welcome',
    resend: 'Send again',
    sentResult: (sent: number, failed: number) =>
      failed ? `${sent} sent, ${failed} failed (try again).` : `${sent} sent.`,
    selectAll: 'Select all',
    selectOne: (e: string) => `Select ${e}`,
    person: 'Person',
    account: 'Account',
    yes: 'Yes',
    notYet: 'Not yet',
    welcome: 'Welcome',
    notSent: 'Not sent',
    sentTimes: (d: string, n: number) => (n > 1 ? `${d} (${n}×)` : d),
    access: 'Access',
    proUntil: (d: string) => `Pro until ${d}`,
    teamUntil: (d: string) => `Team until ${d}`,
    proActive: 'Pro',
    teamActive: 'Team',
    free: 'Free',
    reportsCol: 'Reports',
    grantDays: 'Days of access',
    rowGivePro: 'Give Pro',
    rowGiveTeam: 'Give Team',
    needsAccount: 'No account yet: the person must sign in once (“Start free”).',
    teamGiven: (created: boolean, until: string) =>
      created ? `Team created, on trial until ${until}.` : `Team on trial until ${until}.`,
    copyReportLink: 'Copy report link',
    linkCopied: 'Link copied.',
    actions: 'Actions',
    // Anomalias
    reportsHint: (n: number) => `${n} ${n === 1 ? 'report' : 'reports'} from people testing.`,
    noReports: 'No reports yet. Testers report them from the link in the welcome email.',
    statusFilter: 'Filter by status',
    all: 'All',
    status: (s: 'new' | 'reviewing' | 'resolved') =>
      ({ new: 'New', reviewing: 'Reviewing', resolved: 'Resolved' })[s],
    severity: (s: 'blocker' | 'annoying' | 'suggestion') =>
      ({ blocker: 'Stops work', annoying: 'Gets in the way', suggestion: 'Suggestion' })[s],
    statusOf: (title: string) => `Status of ${title}`,
    steps: 'Steps to reproduce',
    where: 'Where',
    browser: 'Browser',
    attachments: 'Attachments',
    adminNote: 'Internal note',
    adminNoteOf: (title: string) => `Internal note of ${title}`,
    removeReport: 'Remove this report and its attachments?',
    reportRemoved: 'Report removed.',
    reply: 'Reply by email',
  },
  {
    'pt-PT': {
      noAccess: 'Sem acesso',
      noAccessHint: 'O portal de administração é só para o proprietário da aplicação.',
      title: 'Administração',
      hint: 'Regras dos sites, registos e os totais da plataforma. Os dados internos de cada empresa não aparecem aqui.',
      section: 'Secção',
      tabSummary: 'Resumo e poupança',
      tabSites: 'Sites',
      tabLogs: 'Registos',
      estimatedSavings: 'Poupança estimada',
      stepsTimes: (n) => `${n} passos × 2 min`,
      documentedSteps: 'Passos documentados',
      guides: 'Guias',
      accounts: 'Contas',
      teams: 'Equipas',
      unlocks30: 'Desbloqueios (30 dias)',
      totalsNote:
        'Não é uma medição. Totais de toda a plataforma: os dados de cada empresa (espaços, pessoas, guias) só os vê quem pertence a ela.',
      savingsByMonth: 'Poupança estimada por mês',
      month: 'Mês',
      rulesHint:
        'Sites onde a extensão não grava. «dominio.pt» cobre o domínio e os subdomínios; «dominio.pt/caminho» só esse caminho. As alterações chegam às extensões em poucas horas (ou logo, ao ligar a conta).',
      ruleExists: 'Já existe uma regra para esse site.',
      newRule: 'Nova regra',
      site: 'Site',
      sitePlaceholder: 'ex.: intranet.empresa.pt',
      restriction: 'Restrição',
      protected: 'Protegido',
      locked: 'Bloqueado',
      note: 'Nota',
      noteOptional: 'Nota (opcional)',
      add: 'Adicionar',
      ruleAdded: 'Regra adicionada.',
      rulesCount: (n) => `${n} regras`,
      searchRules: 'Procurar regras',
      search: 'Procurar…',
      restrictionOf: (p) => 'Restrição de ' + p,
      noteOf: (p) => 'Nota de ' + p,
      ruleUpdated: 'Regra atualizada.',
      noteSaved: 'Nota guardada.',
      removeOf: (p) => 'Remover ' + p,
      removeRule: 'Remover a regra?',
      cancel: 'Cancelar',
      remove: 'Remover',
      ruleRemoved: 'Regra removida.',
      modeLong: (m) =>
        m === 'locked'
          ? 'Bloqueado (só o proprietário desbloqueia)'
          : 'Protegido (cada pessoa pode desbloquear, com confirmação)',
      modeShort: (m) => (m === 'locked' ? 'bloqueado' : 'protegido'),
      action: (a) =>
        ({
          'rule.create': 'Regra criada',
          'rule.update': 'Regra alterada',
          'rule.delete': 'Regra removida',
          'site.unlock': 'Site desbloqueado na extensão',
          'site.relock': 'Site bloqueado de novo na extensão',
          'grant.pro': 'Teste do Pro dado',
          'grant.revoke': 'Teste do Pro retirado',
          'grant.team': 'Teste da equipa prolongado',
          'beta.settings': 'Definições do programa beta guardadas',
          'beta.welcome': 'Boas-vindas enviadas',
        })[a] ?? a,
      noteChanged: 'nota alterada',
      when: 'Quando',
      who: 'Quem',
      what: 'O quê',
      detail: 'Detalhe',
      anonymous: 'Anónimo',
      noLogs: 'Ainda não há registos.',
      tabSignups: 'Programa beta',
      signupsHint: (n) =>
        `${n} ${n === 1 ? 'pessoa pediu' : 'pessoas pediram'} para testar (página /teste).`,
      noSignups: 'Ainda não há inscrições.',
      copyEmails: 'Copiar emails',
      copied: 'Emails copiados.',
      email: 'Email',
      name: 'Nome',
      company: 'Empresa',
      teamSize: 'Equipa',
      area: 'Área',
      tools: 'Aplicações que usam',
      tabAccess: 'Acessos',
      target: 'Site ou conta',
      accessHint:
        'Dê acesso a quem testa, sem pagamentos: a pessoa tem de ter conta (ter entrado uma vez). Uma subscrição paga nunca é alterada.',
      days: 'Dias',
      emailPlaceholder: 'nome@empresa.pt',
      giveProTitle: 'Teste do Pro',
      giveProHint:
        'Plano Pro para uma pessoa até à data escolhida. Nunca encurta um teste mais longo.',
      givePro: 'Dar teste do Pro',
      proGiven: (email, until) => `${email} tem Pro até ${until}.`,
      teamTitle: 'Prolongar o teste da equipa',
      teamHint: 'Prolonga o teste de todas as equipas de que a pessoa é dona.',
      extendTeam: 'Prolongar teste da equipa',
      teamExtended: (n, until) => `${n === 1 ? '1 equipa' : `${n} equipas`} em teste até ${until}.`,
      noAccount: 'Não há nenhuma conta com esse email. Peça à pessoa para entrar uma vez primeiro.',
      noTeam: 'Essa conta não é dona de nenhuma equipa (tem de criar uma primeiro).',
      paidPlan: 'Essa conta tem uma subscrição paga: nada foi alterado.',
      grantsTitle: 'Pro dado por si',
      noGrants: 'Ainda não deu nenhum teste do Pro.',
      until: 'Até',
      expired: 'terminou',
      revoke: 'Retirar',
      revokeOf: (e) => `Retirar o Pro a ${e}`,
      revokeConfirm: (e) => `Retirar o Pro a ${e}? A conta volta ao plano gratuito.`,
      revoked: 'Pro retirado.',
      tabReports: 'Anomalias',
      betaSettingsTitle: 'Link da extensão e email de boas-vindas',
      betaSettingsHint:
        'Quando a extensão estiver publicada, cole aqui o link. Cada pessoa recebe as boas-vindas na sua língua, com uma ligação pessoal para nos contar problemas.',
      extensionUrl: 'Link da extensão',
      extensionUrlPlaceholder: 'https://chromewebstore.google.com/detail/…',
      macUrl: 'Descarga da app de Mac (DMG)',
      macUrlPlaceholder: 'https://downloads.guides.nolens.app/NolensGuides.dmg',
      macUrlHint: 'Aparece na página «App para Mac». Só versão de teste: ainda sem notarização.',
      language: 'Língua',
      langPt: 'Português',
      langEn: 'English',
      subject: 'Assunto',
      body: 'Texto',
      markers:
        'Marcadores: {nome} nome · {email} · {link_extensao} link da extensão · {link_entrar} página para entrar · {link_reportar} ligação pessoal para nos contar problemas.',
      preview: 'Pré-visualização (exemplo)',
      previewName: 'Ana',
      save: 'Guardar',
      saved: 'Guardado.',
      restoreDefault: 'Repor o texto por omissão',
      sendTest: 'Enviar um teste para mim',
      testSent: 'Teste enviado para o seu email.',
      unsaved: 'Tem alterações por guardar: guarde-as antes de enviar.',
      noExtensionUrl: 'Indique e guarde o link da extensão antes de enviar as boas-vindas.',
      notConfigured: 'O envio de emails não está configurado (RESEND_API_KEY).',
      sendSelected: (n) => `Enviar boas-vindas aos selecionados (${n})`,
      sendPending: (n) => `Enviar a quem ainda não recebeu (${n})`,
      confirmSend: (n) => `Enviar o email de boas-vindas a ${n} ${n === 1 ? 'pessoa' : 'pessoas'}?`,
      send: 'Enviar',
      sendOne: 'Enviar boas-vindas',
      resend: 'Reenviar',
      sentResult: (sent, failed) =>
        failed ? `${sent} enviados, ${failed} falharam (tente de novo).` : `${sent} enviados.`,
      selectAll: 'Selecionar todos',
      selectOne: (e) => `Selecionar ${e}`,
      person: 'Pessoa',
      account: 'Conta',
      yes: 'Sim',
      notYet: 'Ainda não',
      welcome: 'Boas-vindas',
      notSent: 'Por enviar',
      sentTimes: (d, n) => (n > 1 ? `${d} (${n}×)` : d),
      access: 'Acesso',
      proUntil: (d) => `Pro até ${d}`,
      teamUntil: (d) => `Equipa até ${d}`,
      proActive: 'Pro',
      teamActive: 'Equipa',
      free: 'Grátis',
      reportsCol: 'Anomalias',
      grantDays: 'Dias de acesso',
      rowGivePro: 'Dar Pro',
      rowGiveTeam: 'Dar Equipa',
      needsAccount: 'Ainda sem conta: a pessoa tem de entrar uma vez («Começar grátis»).',
      teamGiven: (created, until) =>
        created ? `Equipa criada, em teste até ${until}.` : `Equipa em teste até ${until}.`,
      copyReportLink: 'Copiar ligação para anomalias',
      linkCopied: 'Ligação copiada.',
      actions: 'Ações',
      reportsHint: (n) =>
        `${n} ${n === 1 ? 'anomalia comunicada' : 'anomalias comunicadas'} por quem está a testar.`,
      noReports:
        'Ainda não há anomalias. Quem testa comunica-as pela ligação do email de boas-vindas.',
      statusFilter: 'Filtrar por estado',
      all: 'Todas',
      status: (s) => ({ new: 'Nova', reviewing: 'Em análise', resolved: 'Resolvida' })[s],
      severity: (s) =>
        ({ blocker: 'Impede o trabalho', annoying: 'Atrapalha', suggestion: 'Sugestão' })[s],
      statusOf: (title) => `Estado de ${title}`,
      steps: 'Passos para reproduzir',
      where: 'Onde',
      browser: 'Browser',
      attachments: 'Anexos',
      adminNote: 'Nota interna',
      adminNoteOf: (title) => `Nota interna de ${title}`,
      removeReport: 'Remover esta anomalia e os anexos?',
      reportRemoved: 'Anomalia removida.',
      reply: 'Responder por email',
    },
  },
);
